import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import SliceBase from "store/types/SliceBase";
import { Role } from "util/config/roles";
import Approver from "../../entities/Approver";
import WorkflowType from "../../entities/WorkflowType";
import i18n from "../../i18n";
import WorkflowKind from "../../util/config/workflowKind";
import WorkflowStatus from "../../util/config/workflowStatus";
import { apiCallBegan, ApiCallFailedPayload } from "../api";
import { AppDispatch, AppState } from "../configureStore";
import ResponseBase from "../types/ResponseBase";
import SliceCollectionBase from "../types/SliceCollectionBase";
import { authHeader, getWorkerId } from "../users/auth";
import { getEnumValueByKeyStrict } from "components/utils";

// RESPONSE DECLARATIONS
interface AssignableApproverResponse extends ResponseBase {
  Name: string;
}

interface WorkflowTypeResponse extends ResponseBase {
  CompanyId: number;
  Kind: keyof typeof WorkflowKind;
  Enabled: boolean;
  Code?: string;
  Name?: string;
}

interface WorkflowResponse extends ResponseBase {
  WorkflowId: number;
  WorkerId: number;
  WorkerCode: string; //from AX, worker identificator in AX.
  WorkerName: string;
  ContractId?: number;
  ContractPositionId?: number;
  ContractPositionName?: string;
  CompanyId?: number;
  CompanyName?: string;
  OrganizationUnitId?: number;
  OrganizationUnitName?: string;
  Kind: WorkflowKind;
  Status: WorkflowStatus;
  Sequence: number;
  Notes?: string;
  LeaveDays?: number;
  ApproverId?: number;
  ApproverName?: string;
  ApprovedByRole?: string;
  AssignedTo?: number;
  CreatedAt?: Date;
  StatusConfirmedAt?: Date;
  EffectiveDate: Date;
  Steps: WorkflowResponseStep[];
  Data: WorkflowResponseData[];
}

interface WorkflowResponseStep extends ResponseBase {
  WorkflowId: number;
  RoleId: string;
  Status: WorkflowStatus;
  Sequence: number;
  Notes?: string;
  ApproverId?: number;
  ApproverName?: string;
  ApprovedByRole?: string;
  AssignedTo?: number;
  CreatedAt?: Date;
  StatusConfirmedAt?: Date;
}

interface WorkflowResponseData {
  WorkflowId: number;
  TableName: string;
  RecordId: number;
  PropertyName: string;
  OldPropertyValue?: string;
  OldPropertyName?: string;
  NewPropertyValue?: string;
  NewPropertyName?: string;
}

type ClassificatorResponse<T> = T[] | { value: T[] };

// SLICE
interface WorkflowCount extends SliceBase {
  leave: number;
}

interface State {
  assignableApprovers: SliceCollectionBase<Approver>;
  types: SliceCollectionBase<WorkflowType>;
  count: WorkflowCount;
  forApprover: SliceCollectionBase<WorkflowResponse>;
  countForApprover: WorkflowCount;
  reRequestData: number;
  kindsForWorker: SliceCollectionBase<WorkflowKind>;
}

const initialState: State = {
  assignableApprovers: {
    list: [],
    loading: false,
    lastFetch: null,
    error: null
  },
  types: {
    list: [],
    loading: false,
    lastFetch: null,
    error: null
  },
  count: {
    leave: 0,
    loading: false,
    lastFetch: null,
    error: null
  },
  forApprover: {
    list: [],
    loading: false,
    lastFetch: null,
    error: null
  },
  countForApprover: {
    leave: 0,
    loading: false,
    lastFetch: null,
    error: null
  },
  kindsForWorker: {
    list: [],
    loading: false,
    lastFetch: null,
    error: null
  },
  reRequestData: Date.now()
};

const slice = createSlice({
  name: "workflows",
  initialState,
  reducers: {
    // REQUESTED APPROVERS
    approversRequested: workflows => {
      workflows.assignableApprovers.loading = true;
      workflows.assignableApprovers.error = null;
    },
    approversReceived: (workflows, action: PayloadAction<AssignableApproverResponse[]>) => {
      workflows.assignableApprovers.list = assignedApproversSchema(action.payload);
      workflows.assignableApprovers.loading = false;
      workflows.assignableApprovers.lastFetch = Date.now();
    },
    approversRequestFailed: (workflows, action: PayloadAction<ApiCallFailedPayload>) => {
      workflows.assignableApprovers.loading = false;
      workflows.assignableApprovers.error = action.payload;
    },

    // REQUESTED TYPES
    typesRequested: workflows => {
      workflows.types.loading = true;
      workflows.types.error = null;
    },
    typesReceived: (workflows, action: PayloadAction<ClassificatorResponse<WorkflowTypeResponse>>) => {
      workflows.types.list = typesSchema(unwrapClassificatorResponse(action.payload));
      workflows.types.loading = false;
      workflows.types.lastFetch = Date.now();
    },
    typesRequestFailed: (workflows, action: PayloadAction<ApiCallFailedPayload>) => {
      workflows.types.loading = false;
      workflows.types.error = action.payload;
    },

    // REQUESTED WORKFLOW COUNT
    workflowCountRequested: workflows => {
      workflows.count.loading = true;
      workflows.count.error = null;
    },
    workflowCountReceived: (workflows, action: PayloadAction<{ value: number; extraData: WorkflowKind }>) => {
      // if (action.payload.extraData === WorkflowKind.Total) {
      //   workflows.count.total = action.payload.value;
      // }

      if (action.payload.extraData === WorkflowKind.Leave) {
        workflows.count.leave = action.payload.value;
      }

      workflows.count.loading = false;
      workflows.count.lastFetch = Date.now();
    },
    workflowCountRequestFailed: (workflows, action: PayloadAction<ApiCallFailedPayload>) => {
      workflows.count.loading = false;
      workflows.count.error = action.payload;
    },

    // REQUESTED WORKFLOWS
    approverWorkflowRequested: workflows => {
      workflows.forApprover.loading = true;
      workflows.forApprover.error = null;
    },
    approverWorkflowReceived: (workflows, action: PayloadAction<WorkflowResponse[]>) => {
      workflows.forApprover.list = action.payload;
      workflows.forApprover.loading = false;
      workflows.forApprover.lastFetch = Date.now();
    },
    approverWorkflowFailed: (workflows, action: PayloadAction<ApiCallFailedPayload>) => {
      workflows.forApprover.loading = false;
      workflows.forApprover.error = action.payload;
    },

    // REQUESTED WORKFLOW COUNT FOR APPROVER
    activeWorkflowCountRequested: workflows => {
      workflows.count.loading = true;
      workflows.count.error = null;
    },
    activeWorkflowCountReceived: (workflows, action: PayloadAction<{ value: number; extraData: WorkflowKind }>) => {
      // if (action.payload.extraData === WorkflowKind.Total) {
      //   workflows.count.total = action.payload.value;
      // }

      if (action.payload.extraData === WorkflowKind.Leave) {
        workflows.count.leave = action.payload.value;
      }

      workflows.count.loading = false;
      workflows.count.lastFetch = Date.now();
    },
    activeWorkflowCountRequestFailed: (workflows, action: PayloadAction<ApiCallFailedPayload>) => {
      workflows.count.loading = false;
      workflows.count.error = action.payload;
    },
    workflowChanged: (workflows, action: PayloadAction) => {
      workflows.reRequestData = Date.now();
    },
    // WORKFLOW KINDS FOR WORKER
    kindsForWorkerRequested: workflows => {
      workflows.kindsForWorker.loading = true;
      workflows.kindsForWorker.error = null;
    },
    kindsForWorkerReceived: (workflows, action: PayloadAction<WorkflowKind[]>) => {
      workflows.kindsForWorker.list = action.payload;
      workflows.kindsForWorker.loading = false;
      workflows.kindsForWorker.lastFetch = Date.now();
    },
    kindsForWorkerRequestFailed: (workflows, action: PayloadAction<ApiCallFailedPayload>) => {
      workflows.kindsForWorker.loading = false;
      workflows.kindsForWorker.error = action.payload;
    }
  }
});

export default slice.reducer;

const {
  approversRequested,
  approversReceived,
  approversRequestFailed,

  typesRequested,
  typesReceived,
  typesRequestFailed,

  workflowCountRequested,
  workflowCountReceived,
  workflowCountRequestFailed,

  approverWorkflowRequested,
  approverWorkflowReceived,
  approverWorkflowFailed,

  activeWorkflowCountRequested,
  activeWorkflowCountReceived,
  activeWorkflowCountRequestFailed,

  kindsForWorkerRequested,
  kindsForWorkerReceived,
  kindsForWorkerRequestFailed,

  workflowChanged
} = slice.actions;

// ACTION CREATORS //
export const approveWorkflow =
  (workflowId: number, status: number, reason?: string) => async (dispatch: AppDispatch) => {
    const url = `/api/Workflow/Approve`;
    return await dispatch(
      apiCallBegan({
        url,
        method: "POST",
        data: { workflowId, reason, status },
        headers: { ...authHeader(), "Accept-Language": i18n.language },
        onSuccess: workflowChanged.type
      })
    );
  };

export const rejectWorkflow =
  (workflowId: number, status: number, reason?: string) => async (dispatch: AppDispatch) => {
    const url = `/api/Workflow/Reject`;

    return await dispatch(
      apiCallBegan({
        url,
        method: "POST",
        data: { workflowId, reason, status },
        headers: { ...authHeader(), "Accept-Language": i18n.language },
        onSuccess: workflowChanged.type
      })
    );
  };

export const getAssignableApprovers =
  (workflowKind: WorkflowKind) => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (getState().entities.workflows.assignableApprovers.loading || workflowKind === undefined) {
      return;
    }

    const url = `/api/workflow/GetAssignableApprovers/${workflowKind}`;

    return await dispatch(
      apiCallBegan({
        url,
        method: "GET",
        headers: { ...authHeader(), "Accept-Language": i18n.language },
        onStart: approversRequested.type,
        onSuccess: approversReceived.type,
        onError: approversRequestFailed.type
      })
    );
  };

export const getWorkflowTypes = () => async (dispatch: AppDispatch, getState: () => AppState) => {
  if (getState().entities.workflows.types.loading) {
    return;
  }

  const companyId = getState().users.auth.data?.CompanyId;

  const queryParams = new URLSearchParams();
  queryParams.append("companyId", String(companyId));
  queryParams.append("enabled", "true");
  const url = `/api/classificator/workflow-types?${queryParams.toString()}`;

  return await dispatch(
    apiCallBegan({
      url,
      method: "GET",
      headers: { ...authHeader(), "Accept-Language": i18n.language },
      onStart: typesRequested.type,
      onSuccess: typesReceived.type,
      onError: typesRequestFailed.type
    })
  );
};

export const getWorkflowKindsForWorker = () => async (dispatch: AppDispatch, getState: () => AppState) => {
  if (getState().entities.workflows.kindsForWorker.loading) {
    return;
  }

  const currentWorkerId = getWorkerId(getState());
  const queryParams = new URLSearchParams();
  if (currentWorkerId !== undefined) {
    queryParams.append("workerId", currentWorkerId.toString());
  } else return;
  const url = `/api/workflow/GetActiveApprovalWorkflowKinds?${queryParams.toString()}`;

  return await dispatch(
    apiCallBegan({
      url,
      method: "GET",
      headers: { ...authHeader(), "Accept-Language": i18n.language },
      onStart: kindsForWorkerRequested.type,
      onSuccess: kindsForWorkerReceived.type,
      onError: kindsForWorkerRequestFailed.type
    })
  );
};

//TODO: Test if this endpoint works and gives correct data
export const getWorkflowCount =
  (kinds: WorkflowKind[], roles: Role[]) => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (getState().entities.workflows.count.loading) {
      return;
    }

    const currentWorkerId = getWorkerId(getState());

    // const url = `/api/workflow/GetActiveApprovalWorkflowCount/workerId=${workerId ?? currentWorkerId}`;

    const queryParams = new URLSearchParams();
    if (currentWorkerId !== undefined) {
      queryParams.append("workerId", currentWorkerId.toString());
    } else {
      return;
    }

    roles.forEach(role => queryParams.append("role", role));
    kinds.forEach(kind => queryParams.append("kind", kind.toString()));
    const url = `/api/Workflow/GetActiveWorkflowCountForApprover?${queryParams.toString()}`;

    return await dispatch(
      apiCallBegan({
        url,
        method: "GET",
        headers: { ...authHeader(), "Accept-Language": i18n.language },
        onStart: workflowCountRequested.type,
        onSuccess: workflowCountReceived.type,
        onError: workflowCountRequestFailed.type,
        extraData: WorkflowKind.Leave
      })
    );
  };

export const getApproverWorkflows =
  (roles: Role[], kinds: WorkflowKind[], data?: boolean) => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (getState().entities.workflows.count.loading) {
      return;
    }

    const currentWorkerId = getWorkerId(getState());
    const queryParams = new URLSearchParams();

    if (currentWorkerId !== undefined) {
      queryParams.append("workerId", currentWorkerId.toString());
      queryParams.append("data", "true");
    } else return;

    roles.forEach(role => queryParams.append("role", role));
    kinds.forEach(kind => queryParams.append("kind", kind.toString()));
    if (data) queryParams.append("data", data.toString());

    const url = `/api/Workflow/GetApproverWorkflows?${queryParams.toString()}`;

    const result = await dispatch(
      apiCallBegan({
        url,
        method: "GET",
        headers: { ...authHeader(), "Accept-Language": i18n.language },
        onStart: approverWorkflowRequested.type,
        onSuccess: approverWorkflowReceived.type,
        onError: approverWorkflowFailed.type
      })
    );
    return result;
  };

export const getActiveWorkflowCountForApprover =
  (roles: Role[], kinds: WorkflowKind[]) => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (getState().entities.workflows.count.loading) {
      return;
    }

    const currentWorkerId = getWorkerId(getState());
    const queryParams = new URLSearchParams();

    if (currentWorkerId !== undefined) {
      queryParams.append("workerId", currentWorkerId.toString());
    } else return;

    roles.forEach(role => queryParams.append("role", role));
    kinds.forEach(kind => queryParams.append("kind", kind.toString()));

    const url = `/api/Workflow/GetActiveWorkflowCountForApprover?${queryParams.toString()}`;

    const result = await dispatch(
      apiCallBegan({
        url,
        method: "GET",
        headers: { ...authHeader(), "Accept-Language": i18n.language },
        onStart: activeWorkflowCountRequested.type,
        onSuccess: activeWorkflowCountReceived.type,
        onError: activeWorkflowCountRequestFailed.type
      })
    );
    return result;
  };

// NORMALIZATION //
const assignedApproversSchema = (payload: AssignableApproverResponse[]): Approver[] => {
  if (!payload || payload.length === 0) {
    return [];
  }

  return payload.map(
    (resp: AssignableApproverResponse): Approver => ({
      Id: resp.Id,
      Name: resp.Name
    })
  );
};

const typesSchema = (payload: WorkflowTypeResponse[]): WorkflowType[] => {
  if (!payload || payload.length === 0) {
    return [];
  }

  return payload.map(
    (resp: WorkflowTypeResponse): WorkflowType => ({
      Code: resp.Code,
      CompanyId: resp.CompanyId,
      Enabled: resp.Enabled,
      Id: resp.Id,
      Kind: getEnumValueByKeyStrict(WorkflowKind, resp.Kind),
      Name: resp.Name
    })
  )
};

const unwrapClassificatorResponse = <T>(payload: ClassificatorResponse<T>): T[] => {
  if (Array.isArray(payload)) {
    return payload;
  }

  return payload?.value || [];
};

// SELECTORS //
/**
 * Creates a selector to check if a specific workflow kind is enabled.
 *
 * @param {WorkflowKind} kind - The kind of workflow to check.
 * @returns {Function} - A memoized selector function.
 *
 * @example
 * // Usage with useSelector in a React component
 * import { useSelector } from 'react-redux';
 *
 * const MyComponent = () => {
 *   const isEnabled = useSelector(isKindEnabled(kind));
 * ...
 * };
 */
export const isKindEnabled = (kind: WorkflowKind) => {
  const workflowTypes = (state: AppState) => state.entities.workflows.types.list;

  return createSelector([workflowTypes], types => types.some(type => type.Enabled && type.Kind === kind));
};
